import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone } from 'lucide-react';

export default function About() {
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const scoobyStats = [
    { number: '10+', label: 'Years Active' },
    { number: '500+', label: 'Shows Played' },
    { number: '50K+', label: 'Fans' },
    { number: '15+', label: 'Albums Released' }
  ];

  const siriusStats = [
    { number: '2018', label: 'Founded' },
    { number: 'DMV', label: 'Based' },
    { number: '100s', label: 'Live Performances' },
    { number: 'Multi', label: 'Genre Sound' }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section - Scooby DaGodSon */}
      <motion.section
        className="py-32 px-6 bg-gradient-to-b from-[#0a0a0a] to-black"
        initial="hidden"
        animate="visible"
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Scooby DaGodSon</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Keeping the DMV Go-Go legacy alive for new generations
          </p>
        </div>
      </motion.section>

      {/* Scooby DaGodSon Bio */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Born in the heart of Washington DC, Scooby DaGodSon is one of the most authentic voices in modern Go-Go. His sound blends classic, percussion-driven Go-Go with elements of rap and R&B, creating high-energy performances that honor the culture while pushing it forward.
              </p>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Deeply rooted in the DMV, Scooby has performed across clubs, festivals, and major stages, bringing communities together through music. Respected by Go-Go legends and embraced by new fans, he represents the past, present, and future of the sound.
              </p>
            </motion.div>

            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/band.jpg"
                  alt="Scooby DaGodSon"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 shadow-2xl">
                <div className="text-white text-center">
                  <div className="text-3xl font-bold">DMV</div>
                  <div className="text-sm">Go-Go Legend</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Musical Style Section */}
      <section className="py-20 px-6 bg-black">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-4xl font-bold mb-12 text-center">Musical Style</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center">
                <h3 className="text-xl font-bold text-orange-500 mb-4">Go-Go Roots</h3>
                <p className="text-gray-300 leading-relaxed">
                  Signature call-and-response, live band energy, and heavy percussion that define true Go-Go.
                </p>
              </div>

              <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center">
                <h3 className="text-xl font-bold text-orange-500 mb-4">Modern Fusion</h3>
                <p className="text-gray-300 leading-relaxed">
                  A fresh blend of Go-Go with rap and R&B influences for today's audiences.
                </p>
              </div>

              <div className="bg-[#1a1a1a] rounded-2xl p-8 text-center">
                <h3 className="text-xl font-bold text-orange-500 mb-4">Community First</h3>
                <p className="text-gray-300 leading-relaxed">
                  Every performance is a shared DMV experience that brings people together.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Scooby Stats Section */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="container mx-auto">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.2 }
              }
            }}
          >
            {scoobyStats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center bg-[#1a1a1a] rounded-2xl p-6 hover:bg-[#252525] transition-colors"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">{stat.number}</div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="border-t border-gray-800 mx-auto max-w-4xl" />

      {/* Sirius Company Section */}
      <motion.section
        className="py-32 px-6 bg-black"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-6xl md:text-7xl font-bold mb-6">
            <span className="text-orange-500">Sirius Company</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A powerhouse of modern Go-Go, rooted in D.C.
          </p>
        </div>
      </motion.section>

      {/* Sirius Company Bio */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
              Founded in 2018, Sirius Company is a dynamic Go-Go band blending the raw soul of traditional Go-Go with influences from R&B, hip-hop, and soul. Their sound is bold, contemporary, and unmistakably Washington DC.
            </p>
            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
              Led by Go-Go icon Scooby DaGodSon, Sirius Company delivers electrifying live performances built on deep grooves, hypnotic rhythms, and signature call-and-response. The band is known for honoring tradition while pushing the culture forward, connecting generations through music.
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              From legendary D.C. venues to major festival stages, Sirius Company continues to represent the DMV with authenticity, energy, and impact.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sirius Stats Section */}
      <section className="py-20 px-6 bg-black">
        <div className="container mx-auto">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.2 }
              }
            }}
          >
            {siriusStats.map((stat, index) => (
              <motion.div
                key={index}
                className="text-center bg-[#1a1a1a] rounded-2xl p-6 hover:bg-[#252525] transition-colors"
                variants={fadeInUp}
              >
                <div className="text-4xl md:text-5xl font-bold text-orange-500 mb-2">{stat.number}</div>
                <div className="text-gray-400 text-sm md:text-base">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 px-6 bg-[#0a0a0a]">
        <div className="container mx-auto max-w-2xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-8">Bookings & Contact</h2>
            <div className="space-y-4">
              <a
                href="mailto:siriuscompanydc@gmail.com"
                className="flex items-center justify-center gap-3 text-lg text-gray-300 hover:text-orange-500 transition-colors"
              >
                <Mail className="w-5 h-5" />
                siriuscompanydc@gmail.com
              </a>
              <a
                href="tel:+12027431267"
                className="flex items-center justify-center gap-3 text-lg text-gray-300 hover:text-orange-500 transition-colors"
              >
                <Phone className="w-5 h-5" />
                (202) 743-1267
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <motion.section
        className="py-20 px-6 bg-gradient-to-b from-black to-[#0a0a0a]"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
      >
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Experience the Music Live</h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Don't miss the chance to experience authentic Go-Go music. Book Scooby DaGodSon & Sirius Company for your next event.
          </p>
          <a href="/book">
            <button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold px-12 py-4 text-lg rounded-full transition-all">
              Book Now
            </button>
          </a>
        </div>
      </motion.section>
    </div>
  );
}
